import { useMutation } from "@apollo/client";
import { Dropdown } from "pepsico-ds";
import { useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";
import ToastNotification from "../../components/common/toastNotification/ToastNotification";
import { deleteKeywordGroup } from "../../graphql/mutations/deleteKeywordGroup";
import { updateKeywordGroup } from "../../graphql/mutations/updateKeywordGroup";
import { useProductGroups } from "../../hooks/useProductGroups";
import { useProducts } from "../../hooks/useProducts";
import { useToast } from "../../hooks/useToast";
import ProductFileUploadModal from "./ProductFileUploadModal";
import ProductGroupModal from "./ProductGroupModal";
import ProductGroupsListView from "./ProductGroupsListView";
import ProductsListView from "./ProductsListView";
import RenameProductGroupModal from "./RenameProductGroupModal";
import { VIEW_OPTIONS } from "./productConstants";
import "./productManagement.scss";

export default function ProductManagementPage() {
  const navigate = useNavigate();
  const { products, allProducts, loading, filterData, handlers, refetch } =
    useProducts();

  const {
    productGroups,
    productGroupsFull,
    productGroupMap,
    refetch: refetchGroups,
    renameProductGroup,
    deleteProductGroup,
  } = useProductGroups();

  const { toast, showToast, closeToast } = useToast();

  const [executeDeleteKeyword] = useMutation(deleteKeywordGroup);
  const [executeUpdateKeyword] = useMutation(updateKeywordGroup);
  const [isRenaming, setIsRenaming] = useState(false);

  const [activeView, setActiveView] = useState([VIEW_OPTIONS[0]]);
  const [showFileUploadModal, setShowFileUploadModal] = useState(false);
  const [showGroupModal, setShowGroupModal] = useState(false);
  const [renameModalOpen, setRenameModalOpen] = useState(false);
  const [targetGroup, setTargetGroup] = useState(null);

  const currentView = activeView?.[0]?.id || "products";

  const handleViewChange = (selected) => {
    setActiveView(selected);
  };

  const handleAddSingleProduct = () => {
    navigate("/product-management/create");
  };

  const handleImportFile = () => {
    setShowFileUploadModal(true);
  };

  const handleEditProduct = (product) => {
    navigate(`/product-management/edit/${product.id}`);
  };

  const handleViewProduct = (product) => {
    navigate(`/product-management/edit/${product.id}`);
  };

  const handleDeleteProduct = async (id, name) => {
    try {
      await executeDeleteKeyword({ variables: { id } });
      showToast("success", "Product Deleted", `"${name}" has been deleted`);
      if (refetch) refetch();
    } catch (error) {
      showToast(
        "error",
        "Delete Failed",
        error.message || "Could not delete the product"
      );
    }
  };

  const handleDeleteMultiple = async (productIds) => {
    try {
      await Promise.all(
        productIds.map((productId) =>
          executeDeleteKeyword({ variables: { id: productId } })
        )
      );
      showToast(
        "success",
        "Products Deleted",
        `${productIds.length} products deleted`
      );
      if (refetch) refetch();
    } catch (error) {
      showToast(
        "error",
        "Delete Failed",
        error.message || "Could not delete products"
      );
    }
  };

  const handleFileUploadSuccess = () => {
    showToast("success", "Import Completed", "Product data imported successfully");
    if (refetch) refetch();
  };

  // Product Group handlers
  const handleAddGroup = () => {
    setShowGroupModal(true);
  };

  const handleEditGroup = useCallback((group) => {
    setTargetGroup(group);
    setRenameModalOpen(true);
  }, []);

  const handleRenameClose = () => {
    setRenameModalOpen(false);
    setTargetGroup(null);
  };

  const handleRenameConfirm = async (newName) => {
    if (!targetGroup || !newName || newName === targetGroup.name) {
      return;
    }
    setIsRenaming(true);
    try {
      await renameProductGroup(targetGroup.id, newName);
      showToast(
        "success",
        "Group Renamed",
        `"${targetGroup.name}" has been renamed to "${newName}"`
      );
      handleRenameClose();
    } catch (error) {
      showToast(
        "error",
        "Rename Failed",
        error.message || "Could not rename the product group"
      );
    } finally {
      setIsRenaming(false);
    }
  };

  // View group: filter products by this group's ID
  const handleViewGroup = useCallback(
    (group) => {
      handlers.handleFilterChange({ ...filterData, productGroupId: group.id });
      setActiveView([VIEW_OPTIONS[0]]);
    },
    [handlers, filterData]
  );

  // Assign products to product group(s) by adding group IDs to product's keywordsGroupIds
  const handleSaveGroup = async (groupIds, productIds) => {
    // Normalize to array (handle both single ID and array)
    const ids = Array.isArray(groupIds) ? groupIds : [groupIds];
    try {
      await Promise.all(
        productIds.map(async (productId) => {
          const product = allProducts.find((p) => p.id === productId);
          if (!product) return;
          const existingGroupIds = product.keywordsGroupIds || [];
          const newGroupIds = ids.filter((gid) => !existingGroupIds.includes(gid));
          if (newGroupIds.length === 0) return;
          await executeUpdateKeyword({
            variables: {
              id: productId,
              input: {
                name: product.name,
                slug: product.slug,
                type: product.type,
                keywords: product.keywords,
                imageUrl: product.imageUrl,
                metadata: product.metadata,
                keywordsGroupIds: [...existingGroupIds, ...newGroupIds],
              },
            },
          });
        })
      );

      const groupNames = ids.map((gid) => productGroupMap[gid] || gid).join(", ");
      showToast(
        "success",
        "Product Group Updated",
        `Products have been added to group(s) "${groupNames}"`
      );

      if (refetch) refetch();
    } catch (error) {
      showToast("error", "Error", error.message || "Failed to update product group");
    }
  };

  const handleDeleteGroup = async (group) => {
    // Check if any products reference this group
    const productsInGroup = allProducts.filter((p) =>
      (p.keywordsGroupIds || []).includes(group.id)
    );

    if (productsInGroup.length > 0) {
      showToast(
        "error",
        "Cannot Delete Group",
        `"${group.name}" has ${productsInGroup.length} associated product(s). Remove all products from this group first.`
      );
      return;
    }

    try {
      await deleteProductGroup(group.id);
      showToast(
        "success",
        "Group Removed",
        `Product group "${group.name}" has been removed`
      );
    } catch (error) {
      showToast(
        "error",
        "Delete Failed",
        error.message || "Could not delete the product group"
      );
    }
  };

  return (
    <div className="container">
      <div className="wrap">
        <div className="product-management-container">
          <div className="page-header">
            <h3 className="page-title">
              Available {currentView === "products" ? "Products" : "Product Groups"}
            </h3>
            <div className="page-header-actions">
              <div className="view-switcher-inline">
                <Dropdown
                  childList={VIEW_OPTIONS}
                  selection="single"
                  selectedValue={activeView}
                  placeholder="Display"
                  size="medium"
                  setSelectedValue={handleViewChange}
                />
              </div>
            </div>
          </div>

          {currentView === "products" ? (
            <ProductsListView
              products={products}
              loading={loading}
              filterData={filterData}
              productGroups={productGroups}
              productGroupMap={productGroupMap}
              handlers={handlers}
              onAddSingleProduct={handleAddSingleProduct}
              onImportFile={handleImportFile}
              onViewProduct={handleViewProduct}
              onEditProduct={handleEditProduct}
              onDeleteProduct={handleDeleteProduct}
              onDeleteMultiple={handleDeleteMultiple}
              onSaveGroup={handleSaveGroup}
            />
          ) : (
            <ProductGroupsListView
              productGroups={productGroupsFull}
              allProducts={allProducts}
              loading={loading}
              onViewGroup={handleViewGroup}
              onAddGroup={handleAddGroup}
              onEditGroup={handleEditGroup}
              onDeleteGroup={handleDeleteGroup}
              showToast={showToast}
            />
          )}
        </div>

        <ToastNotification {...toast} handleClose={closeToast} />

        <ProductFileUploadModal
          isOpen={showFileUploadModal}
          onClose={() => setShowFileUploadModal(false)}
          onSuccess={handleFileUploadSuccess}
        />

        <ProductGroupModal
          isOpen={showGroupModal}
          onClose={() => setShowGroupModal(false)}
          onSave={handleSaveGroup}
          onGroupCreated={() => {
            refetchGroups();
          }}
        />

        <RenameProductGroupModal
          isOpen={renameModalOpen}
          targetGroup={targetGroup?.name}
          onClose={handleRenameClose}
          onConfirm={handleRenameConfirm}
          loading={isRenaming}
        />
      </div>
    </div>
  );
}
